<script lang="ts" setup>
import { ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useProject, useProjectWorkflows, useExportProject, useProjectWizardSteps, useProjectConnectionBindings, useDeleteProject } from "@/queries/projects";
import { useSchedules } from "@/queries/schedules";
import { getProjectStatus, getProjectStatusClass } from "../utils/projectStatus";
import { useProducts, useSubscribedProducts } from "@/queries/products";
import { useAlertStore } from "@/stores/alerts-store";
import { useRecentlyViewed } from "@/composables/useRecentlyViewed";
import OverviewTab from "./tabs/OverviewTab.vue";
import WorkflowsTab from "./tabs/WorkflowsTab.vue";
import ConnectionsTab from "./tabs/ConnectionsTab.vue";
import DataStoresTab from "./tabs/DataStoresTab.vue";
import HistoryTab from "./tabs/HistoryTab.vue";
import SchedulesTab from "./tabs/SchedulesTab.vue";
import TestingTab from "./tabs/TestingTab.vue";
import VersionsTab from "./tabs/VersionsTab.vue";
import WizardSequenceEditor from "@/views/publisher/components/tabs/WizardSequenceEditor.vue";
import ProjectWizard from "@/views/shared/components/wizard/ProjectWizard.vue";
import UpgradeAvailableBanner from "./UpgradeAvailableBanner.vue";
import DeleteProjectModal from "./modals/DeleteProjectModal.vue";
import ConvertToProductModal from "./modals/ConvertToProductModal.vue";
import PageHeader from "@/components/PageHeader.vue";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";

const route = useRoute();
const router = useRouter();

const projectId = computed(() => route.params.projectid as string);

const getActiveTabFromRoute = () => {
  // Extract the tab name from the route
  const routeName = route.name as string;
  if (routeName?.startsWith("project-")) {
    const tab = routeName.replace("project-", "");
    // Handle workflow detail route - should show workflows tab
    if (tab === "workflow-detail") {
      return "workflows";
    }
    // History deep-link routes (project-history-run, project-history-service)
    // all surface the History tab.
    if (tab.startsWith("history")) {
      return "history";
    }
    return tab;
  }
  return "overview";
};
const activeTab = ref(getActiveTabFromRoute());
const workflowsEnabled = computed(() => activeTab.value === "workflows");

const { project, projectLoading: loading, projectError: error } = useProject(projectId);
const { projectWorkflows: workflows, projectWorkflowsLoading: workflowsLoading } = useProjectWorkflows(projectId, workflowsEnabled);

// Project schedules — used by both the Schedules tab and the Workflows tab's "scheduled" indicator.
// Fetched lazily when either tab is active; grouped workflowId → schedule ids for the indicator.
const schedulesEnabled = computed(() => activeTab.value === "workflows" || activeTab.value === "schedules");
const { schedules: projectSchedules } = useSchedules(projectId, schedulesEnabled);
const scheduleIdsByWorkflowId = computed(() => {
  const map: Record<string, string[]> = {};
  for (const s of projectSchedules.value) (map[s.workflowId] ??= []).push(s.id);
  return map;
});
const { products } = useProducts();
const { subscribedProducts, subscribedProductsLoading } = useSubscribedProducts();

// Wizard completion drives the status badge's "Setup Required" state. Enabled whenever a project
// is loaded; the query handler short-circuits for DIY (returns empty sequence + complete summary).
const wizardEnabled = computed(() => !!projectId.value);
const { wizardSteps } = useProjectWizardSteps(projectId, wizardEnabled);

// Connection-slot bindings — fetched whenever a project is loaded (not lazily to the Connections tab)
// so the tab can show a count of slots still needing a binding.
const connectionsEnabled = computed(() => !!projectId.value);
const { bindings: connectionBindings } = useProjectConnectionBindings(projectId, connectionsEnabled);
const unboundConnectionCount = computed(
  () => (connectionBindings.value?.slots ?? []).filter((s) => !s.boundConnectionId).length,
);

// Setup Required fires when the project isn't run-ready. Readiness covers wizard + connections
// only — datastores are runtime state and deliberately don't gate the badge.
const projectStatusLabel = computed(() => getProjectStatus(project.value, project.value?.isReady));

// Drives the Configure-button count badge, mirroring the Connections tab's unbound-slot badge.
const incompleteWizardCount = computed(() => {
  const summary = wizardSteps.value?.completionSummary;
  if (!summary) return 0;
  return Math.max(0, summary.totalWorkflowCount - summary.completedWorkflowCount);
});

// Names the actual outstanding gaps for the badge tooltip — so it explains itself rather than the
// old wizard-only "1/1 configured" that read as done while connections were the real blocker.
const setupTooltip = computed(() => {
  const parts: string[] = [];
  const wizardGap = incompleteWizardCount.value;
  if (wizardGap > 0) {
    parts.push(`${wizardGap} ${wizardGap === 1 ? "workflow" : "workflows"} to configure`);
  }
  const connectionGap = unboundConnectionCount.value;
  if (connectionGap > 0) {
    parts.push(`${connectionGap} ${connectionGap === 1 ? "connection" : "connections"} to assign`);
  }
  if (parts.length === 0) return "Finish project setup";
  return `${parts.join(", ")} — click to finish setup`;
});

const validTabs = computed(() => {
  const baseTabs = [
    "overview",
    "workflows",
    "connections",
    "datastores",
    "history",
    "schedules",
    "testing",
  ];
  if (isPublisherMasterProject.value) {
    baseTabs.push("wizard");
    baseTabs.push("product");
  }
  return baseTabs;
});





const showConfigWizard = ref(false);

// initialized: true once project data has arrived
const initialized = computed(() => !loading.value && !!project.value);

// Project name from fetched data
const projectName = computed(() => project.value?.name ?? '');

const { trackView } = useRecentlyViewed();

watch(
  project,
  (p) => {
    if (!p?.id) return;
    trackView({
      id: p.id,
      name: p.name,
      type: "project",
      route: `/projects/${p.id}`,
      description: `${p.projectType} · ${p.workflowCount} workflow${p.workflowCount === 1 ? "" : "s"}`,
      icon: "bi-boxes",
      metadata: {
        projectType: p.projectType,
        publisherProductId: p.publisherProductId,
        subscriberProductId: p.subscriberProductId,
      },
    });
  },
  { immediate: true }
);

const productName = computed(() => {
  if (isProductlessProject.value)
    return "No Product";
  const targetId = project?.value?.subscriberProductId || project.value?.publisherProductId;
  // Subscriber orgs won't have the product in `products` (which is the org's *published* products),
  // so also look it up in the subscribed-products list before falling back to "Unknown Product".
  const prod =
    products.value.find((p) => p.id === targetId) ??
    subscribedProducts.value.find((p) => p.id === targetId);
  if (prod) return prod.name;
  // A subscriber project whose product was deleted resolves to nothing once loaded — read as
  // "Discontinued" (the banner explains). Still loading falls through to "Unknown Product".
  return isProductDiscontinued.value ? "Discontinued product" : "Unknown Product";
});

// A subscriber project whose product no longer resolves — the publisher deleted it. We can state
// this confidently only once the subscribed-products query has loaded (otherwise a mid-load project
// would flash "discontinued"). Runs are already blocked server-side by the subscription gate, so the
// banner explains a real, safe state rather than promising anything.
const isProductDiscontinued = computed(() => {
  const productId = project.value?.subscriberProductId;
  if (!productId || subscribedProductsLoading.value) return false;
  const resolved =
    products.value.find((p) => p.id === productId) ??
    subscribedProducts.value.find((p) => p.id === productId);
  return !resolved;
});

const isPublisherMasterProject = computed(() => {


  if (!project.value?.publisherProductId || project.value.publisherProductId === "") return false;
  const product = products.value.find((p) => p.id === project.value.publisherProductId);
  if (!product) return false;

  return product.masterProjectId === project.value.id;
});

const isProductlessProject = computed(() => {

  if(project.value?.projectType == "DIY") return true;

  //if subscriberProductId is null or empty and publisherProductId is null or empty, then it's a productless project
  return (!project.value?.subscriberProductId || project.value.subscriberProductId === "") &&
    (!project.value?.publisherProductId || project.value.publisherProductId === "");
});

const isSubscriberProject = computed(() => project.value?.projectType === "Subscriber");

function goBackToProjects() {
  router.push("/projects");
}

function editProject() {
  if (project.value?.id) {
    router.push(`/projects/${project.value.id}/edit`);
  }
}

function openBuilder() {
  if (project.value?.id) {
    router.push(`/projects/${project.value.id}/builder`);
  }
}

function setActiveTab(tab: string) {
  if (validTabs.value.includes(tab) && projectId.value) {
    activeTab.value = tab;
    router.push(`/projects/${projectId.value}/${tab}`);
  }
}

function openConfigWizard() {
  showConfigWizard.value = true;
}

function closeConfigWizard() {
  showConfigWizard.value = false;
}

const { exportProjectAsync, exportProjectPending } = useExportProject(projectId);
const alertStore = useAlertStore();

function importProject() {
  // Import lives on the projects landing — direct the user there to pick a target product.
  router.push("/projects?import=1");
}

async function exportProject() {
  if (!project.value || exportProjectPending.value) return;
  try {
    const bundle = await exportProjectAsync();
    const json = JSON.stringify(bundle, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const safeName = (project.value.name || "project").replace(/[^a-zA-Z0-9_-]+/g, "-").toLowerCase();
    link.href = url;
    link.download = `${safeName}-export.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    const message = e instanceof Error ? e.message : "Unable to download the project bundle.";
    alertStore.addError("Export failed", message);
  }
}

// Convert to Product modal state. The convert mutation invalidates the projects cache, so the
// useProject query backing this view refreshes automatically once conversion succeeds.
const showConvertModal = ref(false);

function convertToProduct() {
  showConvertModal.value = true;
}

const { deleteProjectAsync, deleteProjectPending } = useDeleteProject();
const showDeleteModal = ref(false);

function requestDelete() {
  if (!project.value) return;
  showDeleteModal.value = true;
}

async function confirmDelete() {
  if (!project.value || deleteProjectPending.value) return;
  try {
    await deleteProjectAsync(project.value.id);
    showDeleteModal.value = false;
    router.push("/projects");
  } catch (e) {
    // 409 = blocked (in-flight runs / published product / active schedules); surface the reason.
    showDeleteModal.value = false;
    const message = (e as { userMessage?: string })?.userMessage
      ?? (e instanceof Error ? e.message : "Unable to delete this project.");
    alertStore.addError("Can't delete project", message);
  }
}

// Watch for route changes to update active tab
watch(
  () => route.name,
  () => {
    activeTab.value = getActiveTabFromRoute();
  }
);

// useProject refetches automatically when projectId changes
// useProjectWorkflows fetches lazily when workflowsEnabled (activeTab === 'workflows')
</script>

<template>
  <main>
    <PanelGrid :columns="1">
      <PanelCard>
        <!-- Full skeleton: initial load -->
        <template v-if="!initialized">
          <SkeletonLoader type="header" />
          <SkeletonLoader type="panel" :lines="6" class="mt-4" />
        </template>

        <!-- Project Content: show after initialized -->
        <template v-else-if="project?.id">
          <PageHeader
            :title="projectName"
            :breadcrumbs="[
              { label: 'Dashboard', href: '/' },
              { label: 'Projects', href: '/projects' },
              { label: projectName, href: `/projects/${projectId}` },
              { label: activeTab.charAt(0).toUpperCase() + activeTab.slice(1) }
            ]"
          >
            <template #actions>
              <!-- Project status — same labels/colors as the dashboard via shared util. Setup
                   Required renders as a button (keyboard/click) and opens the wizard; other
                   statuses are a display-only span. -->
              <button
                  v-if="project && projectStatusLabel === 'Setup Required'"
                  type="button"
                  class="badge border-0 align-self-center small"
                  :class="getProjectStatusClass(projectStatusLabel)"
                  :title="setupTooltip"
                  @click="openConfigWizard"
              >
                {{ projectStatusLabel }}
              </button>
              <span
                  v-else-if="project"
                  class="badge align-self-center small"
                  :class="getProjectStatusClass(projectStatusLabel)"
                  :title="projectStatusLabel"
              >
                {{ projectStatusLabel }}
              </span>
              <button
                  v-if="!isProductlessProject"
                  class="bb-btn-primary-cta"
                  @click="openConfigWizard"
              >
                <i class="bi bi-gear"></i><span class="d-none d-sm-inline ms-1">Configure</span>
                <span
                  v-if="incompleteWizardCount > 0"
                  class="badge bg-warning text-dark ms-1"
                  :title="`${incompleteWizardCount} workflow(s) still need wizard setup`"
                >{{ incompleteWizardCount }}</span>
              </button>

              <div class="dropdown">
                <button
                  class="bb-btn-secondary-cta dropdown-toggle"
                  type="button"
                  data-bs-toggle="dropdown"
                  title="More actions"
                >
                  <i class="bi bi-three-dots"></i>
                </button>
                <ul class="dropdown-menu dropdown-menu-end">
                  <li>
                    <a class="dropdown-item" href="#" @click.prevent="editProject">
                      <i class="bi bi-pencil me-2"></i>
                      Edit
                    </a>
                  </li>
                  <li><hr class="dropdown-divider" /></li>
                  <li>
                    <a class="dropdown-item" href="#" @click.prevent="importProject">
                      <i class="bi bi-upload me-2"></i>
                      Import
                    </a>
                  </li>
                  <li>
                    <a class="dropdown-item" href="#" @click.prevent="exportProject">
                      <i class="bi bi-download me-2"></i>
                      Export
                    </a>
                  </li>
                  <li v-if="isProductlessProject">
                    <hr class="dropdown-divider" />
                  </li>
                  <li v-if="isProductlessProject">
                    <a class="dropdown-item" href="#" @click.prevent="convertToProduct">
                      <i class="bi bi-box-seam me-2"></i>
                      Convert to Product
                    </a>
                  </li>
                  <li>
                    <hr class="dropdown-divider" />
                  </li>
                  <li>
                    <a
                      class="dropdown-item text-danger"
                      href="#"
                      @click.prevent="requestDelete"
                    >
                      <i class="bi bi-trash me-2"></i>
                      Delete
                    </a>
                  </li>
                </ul>
              </div>
            </template>
          </PageHeader>

          <!-- Deleted Notice — a soft-deleted project opened directly (e.g. from the recycle bin). -->
        <div
          v-if="project.status === 'Deleted'"
          class="alert alert-secondary d-flex align-items-center mb-4"
          role="alert"
        >
          <i class="bi bi-trash me-2"></i>
          <div>
            <strong>Deleted Project</strong>
            <p class="mb-0 mt-1">
              This project has been deleted and is read-only. Restore it from Projects › Recycle Bin.
            </p>
          </div>
        </div>

        <!-- Discontinued Notice — the publisher deleted the product this subscriber project was based
             on. Runs are blocked server-side (subscription gate); this explains why. -->
        <div
          v-if="isProductDiscontinued"
          class="alert alert-warning d-flex align-items-center mb-4"
          role="alert"
        >
          <i class="bi bi-exclamation-triangle me-2"></i>
          <div>
            <strong>Product discontinued</strong>
            <p class="mb-0 mt-1">
              The product this project was based on has been discontinued by the publisher. This
              project can no longer run.
            </p>
          </div>
        </div>

        <UpgradeAvailableBanner
          v-if="isSubscriberProject && !isProductDiscontinued"
          :project-id="project.id"
          :enabled="isSubscriberProject"
        />

        <div class="project-tabs">
          <ul class="nav nav-tabs" role="tablist">
            <li class="nav-item" role="presentation">
              <button
                class="nav-link"
                :class="{ active: activeTab === 'overview' }"
                @click="setActiveTab('overview')"
                type="button"
              >
                Overview
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button
                class="nav-link"
                :class="{ active: activeTab === 'workflows' }"
                @click="setActiveTab('workflows')"
                type="button"
              >
                Workflows
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button
                class="nav-link"
                :class="{ active: activeTab === 'connections' }"
                @click="setActiveTab('connections')"
                type="button"
              >
                Connections
                <span
                  v-if="unboundConnectionCount > 0"
                  class="badge bg-warning ms-1"
                  :title="`${unboundConnectionCount} connection slot(s) still need binding`"
                >{{ unboundConnectionCount }}</span>
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button
                class="nav-link"
                :class="{ active: activeTab === 'datastores' }"
                @click="setActiveTab('datastores')"
                type="button"
              >
                Data Stores
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button
                class="nav-link"
                :class="{ active: activeTab === 'history' }"
                @click="setActiveTab('history')"
                type="button"
              >
                Run History
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button
                class="nav-link"
                :class="{ active: activeTab === 'schedules' }"
                @click="setActiveTab('schedules')"
                type="button"
              >
                Schedules
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button
                class="nav-link"
                :class="{ active: activeTab === 'testing' }"
                @click="setActiveTab('testing')"
                type="button"
              >
                Testing
              </button>
            </li>
            <li
              v-if="isPublisherMasterProject"
              class="nav-item"
              role="presentation"
            >
              <button
                class="nav-link"
                :class="{ active: activeTab === 'wizard' }"
                @click="setActiveTab('wizard')"
                type="button"
              >
                Wizard
              </button>
            </li>
            <li
              v-if="isPublisherMasterProject"
              class="nav-item"
              role="presentation"
            >
              <button
                class="nav-link"
                :class="{ active: activeTab === 'product' }"
                @click="setActiveTab('product')"
                type="button"
              >
                Product Versions
              </button>
            </li>
          </ul>
        </div>
  
        <div class="tab-content mt-4">
          <!-- Tab content skeleton: show when loading but header is cached -->
          <SkeletonLoader v-if="loading || (activeTab === 'workflows' && workflowsLoading)" type="panel" :lines="6" />

          <!-- Tab content -->
          <template v-else>
            <OverviewTab
              v-if="activeTab === 'overview'"
              :project="project"
              :product-name="productName"
              :product-version="project.productVersion"
              :is-productless-project="isProductlessProject"
              @open-config-wizard="openConfigWizard"
              @set-active-tab="setActiveTab"
            />
            <WorkflowsTab
              v-else-if="activeTab === 'workflows'"
              :project="project"
              :workflows="workflows"
              :is-productless-project="isProductlessProject"
              :is-publisher-master-project="isPublisherMasterProject"
              :product-name="productName"
              :product-version="project.productVersion"
              :schedule-ids-by-workflow-id="scheduleIdsByWorkflowId"
            />
            <WizardSequenceEditor
              v-else-if="activeTab === 'wizard'"
              :project-id="projectId"
            />
            <VersionsTab
              v-else-if="activeTab === 'product'"
              :project="project"
              :is-publisher-master-project="isPublisherMasterProject"
            />
            <ConnectionsTab
              v-else-if="activeTab === 'connections'"
              :project="project"
            />
            <DataStoresTab
              v-else-if="activeTab === 'datastores'"
              :project="project"
            />
            <HistoryTab v-else-if="activeTab === 'history'" :project="project" />
            <SchedulesTab v-else-if="activeTab === 'schedules'" :project="project" />
            <TestingTab v-else-if="activeTab === 'testing'" :project="project" />
          </template>
        </div>
        </template>

        <!-- No Project Found -->
      <div v-else class="text-center py-5">
        <i class="bi bi-exclamation-circle display-1 text-muted"></i>
        <h2 class="mt-3 text-muted">Project Not Found</h2>
        <p class="text-muted">The requested project could not be found.</p>
        <button class="bb-btn-secondary-cta" @click="goBackToProjects">
          <i class="bi bi-arrow-left me-1"></i> Back to Projects
        </button>
      </div>

      <!-- Project Setup Wizard Modal -->
      <ProjectWizard
        :project="project"
        :show="showConfigWizard"
        @close="closeConfigWizard"
      />
      </PanelCard>
    </PanelGrid>

    <DeleteProjectModal
      :show="showDeleteModal"
      :project-name="project?.name ?? ''"
      :pending="deleteProjectPending"
      @confirm="confirmDelete"
      @cancel="showDeleteModal = false"
    />

    <ConvertToProductModal
      v-model:show="showConvertModal"
      :project="project ?? null"
    />
  </main>
</template>

<style scoped lang="scss">
.project-tabs {
  margin-bottom: 1.5rem;
}
</style>
